import { Alert, Button, Checkbox, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type FormEvent,
  type JSX,
  forwardRef,
  useEffect,
} from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import { CONTACT_ROLE_LABELS, CONTACT_ROLES, type Site } from '../../../customers/api';
import {
  CORPORATE_SITE_VALUE,
  DEFAULT_CONTACT_FORM_VALUES,
  type ContactFieldErrorTarget,
  type ContactFormFields,
  type ContactFormValues,
  contactFormSchema,
} from '../../../customers/contact-form-schema';

export interface ContactFormProps {
  title: string;
  submitLabel: string;
  defaultValues?: ContactFormFields;
  sites: Site[];
  submitting: boolean;
  formError?: string;
  fieldError?: { field: ContactFieldErrorTarget; message: string };
  onCancel: () => void;
  onSubmit: (values: ContactFormValues) => Promise<void>;
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

export function ContactForm({
  title,
  submitLabel,
  defaultValues,
  sites,
  submitting,
  formError,
  fieldError,
  onCancel,
  onSubmit,
}: ContactFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ContactFormFields, unknown, ContactFormValues>({
    resolver: zodResolver(contactFormSchema) as unknown as Resolver<
      ContactFormFields,
      unknown,
      ContactFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_CONTACT_FORM_VALUES,
  });

  useEffect(() => {
    if (fieldError !== undefined) {
      setError(fieldError.field, { type: 'server', message: fieldError.message });
    }
  }, [fieldError, setError]);

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  const siteOptions = [
    { value: CORPORATE_SITE_VALUE, label: 'Corporativo (sin sede)' },
    ...sites.filter((site) => site.isActive).map((site) => ({ value: site.id, label: site.name })),
  ];

  return (
    <Modal open onClose={onCancel} title={title} className="max-w-lg">
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Nombre completo"
          required
          {...register('fullName')}
          {...maybeError(errors.fullName?.message)}
        />

        <SelectField
          label="Rol"
          required
          placeholder="Seleccioná un rol"
          {...register('role')}
          {...maybeError(errors.role?.message)}
          options={CONTACT_ROLES.map((value) => ({ value, label: CONTACT_ROLE_LABELS[value] }))}
        />

        <SelectField
          label="Sede"
          {...register('siteId')}
          {...maybeError(errors.siteId?.message)}
          options={siteOptions}
        />

        <Field
          label="Email"
          type="email"
          {...register('email')}
          {...maybeError(errors.email?.message)}
        />
        <Field label="Teléfono" {...register('phone')} {...maybeError(errors.phone?.message)} />

        <Checkbox label="Recibe notificaciones" {...register('receivesNotifications')} />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface SelectFieldOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  name: string;
  required?: boolean;
  placeholder?: string;
  error?: string;
  options: SelectFieldOption[];
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onBlur: FocusEventHandler<HTMLSelectElement>;
}

/**
 * `forwardRef` required, same finding as `CustomerForm`'s local
 * `SelectField` (`design.md` §7, PR 6 hallazgo 3): a plain function
 * component drops `register()`'s `ref`, and the field always submits empty.
 */
const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, name, required, placeholder, error, options, onChange, onBlur },
  ref,
) {
  const selectId = `contact-form-${name}`;
  const messageId = `${selectId}-message`;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-sm font-medium text-slate-900">
        {label}
        {required === true ? (
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        ) : null}
      </label>
      <select
        id={selectId}
        name={name}
        ref={ref}
        onChange={onChange}
        onBlur={onBlur}
        className={`h-11 rounded-md border px-3 text-sm ${hasError ? 'border-red-500' : 'border-slate-300'}`}
        aria-invalid={hasError}
        aria-describedby={hasError ? messageId : undefined}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hasError ? (
        <p id={messageId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
});
